import { useCallback, useEffect, useState } from 'react';
import { Form, Radio, Select, Typography, Alert } from 'antd';
import { studioLookups } from '../itemsApi';
import { BASE_STOCK_RELATIONSHIPS, isDerivedRelationship } from '../productStudioModes';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// User-friendly "is this item made from another stock material?"
// question. The DB column is `source_stock_item_id`; this UI never
// shows that name. Instead it offers two radio options + a picker.
//
// Controlled via a sibling Form.Item that owns `sourceStockItemId`
// (the parent's Form instance). This component holds the radio
// tri-state in local state and writes/clears the form value when
// the user toggles.
// ─────────────────────────────────────────────────────────────────

export default function BaseStockSelectorCard({
  form,
  currentItemId,
  disabled,
  compact = false
}) {
  const initialFromForm = form.getFieldValue('sourceStockItemId');
  const [relationship, setRelationship] = useState(
    initialFromForm ? 'derived' : 'base'
  );
  const [sources, setSources] = useState([]);
  const [searchQ, setSearchQ] = useState('');

  // When the user flips back to "base stock item", clear the form
  // field so we never silently send a stale UUID.
  useEffect(() => {
    if (relationship === 'base') {
      form.setFieldsValue({ sourceStockItemId: null });
    }
  }, [relationship, form]);

  const loadSources = useCallback(async (q) => {
    try {
      const rows = await studioLookups.sourceStockItems({ q, limit: 50 });
      const filtered = Array.isArray(rows)
        ? rows.filter((r) => !currentItemId || r.id !== currentItemId)
        : [];
      setSources(filtered);
    } catch (_e) { /* parent handles network errors */ }
  }, [currentItemId]);

  useEffect(() => { loadSources(searchQ); }, [searchQ, loadSources]);

  const isDerived = isDerivedRelationship(relationship);

  return (
    <div className={`erp-studio-basestock ${compact ? 'is-compact' : ''}`}>
      <Text strong style={{ fontSize: 13 }}>Made from base stock?</Text>
      <p style={{ margin: '2px 0 8px', fontSize: 11.5, color: '#64748B' }}>
        Use this when this item is a finished or processed variant of another
        stock material. Example: 4MM Toughened Glass with CEP is made from
        4MM Annealed Plain Clear Glass.
      </p>

      <Radio.Group
        value={relationship}
        onChange={(e) => setRelationship(e.target.value)}
        disabled={disabled}
        optionType="button"
        buttonStyle="solid"
      >
        {BASE_STOCK_RELATIONSHIPS.map((opt) => (
          <Radio.Button key={opt.value} value={opt.value}>
            {opt.label}
          </Radio.Button>
        ))}
      </Radio.Group>

      {isDerived ? (
        <div style={{ marginTop: 10 }}>
          <Form.Item
            label="Base stock material"
            name="sourceStockItemId"
            rules={[{ required: true, message: 'Pick the base stock material.' }]}
            style={{ marginBottom: 4 }}
          >
            <Select
              disabled={disabled}
              allowClear
              showSearch
              filterOption={false}
              onSearch={setSearchQ}
              placeholder="Search base stock items by code or name"
              options={sources.map((s) => ({
                value: s.id,
                label: `${s.itemCode || s.item_code} — ${s.itemName || s.item_name}`
              }))}
            />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            style={{ fontSize: 11.5, padding: '6px 10px' }}
            message="An item cannot be its own source — the system blocks that automatically."
          />
        </div>
      ) : null}
    </div>
  );
}
